import type { Graphics } from 'pixi.js';
import { lerpColor } from '../../color';
import type { Body, WorldDress, WorldFx } from '../../world';

const TAU = Math.PI * 2;
const CAP_SPOTS = [-0.34, -0.3, 0.15, 0.22, -0.4, 0.12, 0.38, 0.1, 0.1, -0.08, 0.42, 0.11, 0.05, 0.02, 0.09, -0.46, 0.18, 0.08];
const RING_SPOTS = 8;
const FAIRY = 12;
const RUNES = 24;

function clamp01(x: number): number {
  return Math.max(0, Math.min(1, x));
}

function spotsOf(g: Graphics, body: Body, list: readonly number[], scale: number): void {
  const r = body.radius;
  for (let i = 0; i < list.length; i += 3) {
    const s = list[i + 2]! * r * scale;
    g.ellipse(body.x + list[i]! * r * body.scaleX, body.y + list[i + 1]! * r * body.scaleY, s * body.scaleX, s * 0.86 * body.scaleY);
  }
}

function toadstool(g: Graphics, x: number, y: number, s: number): void {
  g.roundRect(x - s * 0.28, y - s * 0.15, s * 0.56, s * 0.95, s * 0.2);
}

function toadCap(g: Graphics, x: number, y: number, s: number): void {
  g.moveTo(x - s, y - s * 0.1)
    .bezierCurveTo(x - s * 0.95, y - s * 1.05, x + s * 0.95, y - s * 1.05, x + s, y - s * 0.1)
    .quadraticCurveTo(x, y + s * 0.12, x - s, y - s * 0.1)
    .closePath();
}

export const dress: WorldDress = {
  incomingCircle(k, body, color, alpha) {
    const { g, pal } = k;
    const r = body.radius;
    k.terminator(body, lerpColor(color, pal.ink, 0.55), 0.42 * alpha);
    k.arcLine(body, r * 0.86, 0, TAU);
    g.stroke({ width: Math.max(1, r * 0.05), color: lerpColor(color, pal.ink, 0.35), alpha: 0.32 * alpha });
    g.ellipse(body.x - 0.04 * r * body.scaleX, body.y - 0.04 * r * body.scaleY, 0.2 * r * body.scaleX, 0.17 * r * body.scaleY);
    g.fill({ color: lerpColor(color, pal.rim, 0.4), alpha: 0.35 * alpha });
    spotsOf(g, body, CAP_SPOTS, 1);
    g.fill({ color: lerpColor(color, pal.rim, 0.5), alpha: 0.45 * alpha });
    k.rimLight(body, 0.45 * alpha);
  },

  mineCircle(k, body, alpha, width, fill) {
    const { g, pal } = k;
    const r = body.radius;
    k.trace(body);
    g.fill({ color: fill, alpha });
    k.terminator(body, pal.heldShade, 0.5 * alpha);
    k.arcLine(body, r * 0.9, 0, TAU);
    g.stroke({ width: Math.max(1, r * 0.045), color: lerpColor(fill, pal.heldShade, 0.6), alpha: 0.55 * alpha });
    for (let i = 0; i < RING_SPOTS; i++) {
      const a = (i / RING_SPOTS) * TAU - Math.PI / 2;
      const s = r * (i % 2 === 0 ? 0.13 : 0.1);
      g.ellipse(body.x + Math.cos(a) * r * 0.6 * body.scaleX, body.y + Math.sin(a) * r * 0.6 * body.scaleY, s * body.scaleX, s * 0.85 * body.scaleY);
    }
    g.ellipse(body.x, body.y, r * 0.17 * body.scaleX, r * 0.15 * body.scaleY);
    g.fill({ color: pal.heldLight, alpha: 0.55 * alpha });
    k.rimLight(body, 0.6 * alpha);
    k.trace(body);
    g.stroke({ width: width * 0.8, color: k.outline, alpha, join: 'round' });
  },

  target(k, body, alpha, _spread, reach, spin, edge) {
    const { g, pal } = k;
    k.trace(body);
    g.fill({ color: lerpColor(pal.bg, 0x000000, 0.4), alpha: 0.5 * alpha });
    k.trace(body);
    g.fill({ color: pal.spot, alpha: 0.07 * alpha });
    const dot = Math.max(1, k.unit * 0.0045);
    k.ringDots(body, reach * 0.92, RUNES, -spin * 0.3, (x, y, i) => {
      g.circle(x, y, i % 3 === 0 ? dot * 1.5 : dot);
    });
    g.fill({ color: pal.spot, alpha: 0.5 * alpha });
    const s = Math.max(3, k.unit * 0.018);
    const turn = spin * 0.15;
    k.ringDots(body, reach + s * 0.4, FAIRY, turn, (x, y) => {
      toadstool(g, x, y, s);
    });
    g.fill({ color: pal.rim, alpha: 0.7 * alpha });
    k.ringDots(body, reach + s * 0.4, FAIRY, turn, (x, y, i) => {
      if (i % 2 === 0) toadCap(g, x, y, s);
    });
    g.fill({ color: pal.spot, alpha: 0.85 * alpha });
    k.ringDots(body, reach + s * 0.4, FAIRY, turn, (x, y, i) => {
      if (i % 2 === 1) toadCap(g, x, y, s * 0.85);
    });
    g.fill({ color: pal.incoming.circle, alpha: 0.85 * alpha });
    k.trace(body);
    g.stroke({ width: edge, color: pal.rim, alpha: 0.85 * alpha, join: 'round' });
  },

  pip(k, x, y, radius, on, last, alpha, width) {
    const { g, pal } = k;
    const tone = last ? pal.spot : lerpColor(pal.incoming.circle, pal.rim, 0.35);
    if (on) {
      g.circle(x, y, radius * 1.9);
      g.fill({ color: tone, alpha: 0.18 * alpha });
    }
    g.circle(x, y, radius * 1.05);
    if (on) g.fill({ color: tone, alpha });
    g.stroke({ width: width * 0.8, color: pal.rim, alpha: (on ? 0.9 : 0.5) * alpha });
  },

  path(k, points, count, alpha) {
    const { g, pal } = k;
    const amp = Math.max(1.2, k.unit * 0.006);
    const wave = Math.max(14, k.unit * 0.07);
    const step = Math.max(3, k.unit * 0.012);
    let walked = 0;
    g.moveTo(points[0]!, points[1]!);
    for (let i = 2; i < count; i += 2) {
      const x0 = points[i - 2]!;
      const y0 = points[i - 1]!;
      const dx = points[i]! - x0;
      const dy = points[i + 1]! - y0;
      const len = Math.hypot(dx, dy);
      if (len <= 0) continue;
      const nx = -dy / len;
      const ny = dx / len;
      const n = Math.max(2, Math.min(160, Math.floor(len / step)));
      for (let s = 1; s <= n; s++) {
        const t = s / n;
        const d = walked + len * t;
        const off = amp * Math.sin((d / wave) * TAU) * Math.min(1, d / wave);
        g.lineTo(x0 + dx * t + nx * off, y0 + dy * t + ny * off);
      }
      walked += len;
    }
    g.stroke({ width: Math.max(1, k.unit * 0.0035), color: pal.path, alpha: Math.min(1, alpha * 2.4), cap: 'round', join: 'round' });
  },
};

export const fx: WorldFx = {
  bit(g, x, y, size, _i, color, alpha) {
    g.circle(x, y, size * 1.7);
    g.fill({ color, alpha: alpha * 0.2 });
    g.circle(x, y, size * 0.75);
    g.fill({ color, alpha });
  },

  milestone(g, m, pal) {
    const puffs = 18;
    for (let i = 0; i < puffs; i++) {
      const a = (i / puffs) * TAU + 0.2;
      const d = m.r * (0.85 + 0.12 * Math.sin(i * 2.3));
      g.circle(m.x + Math.cos(a) * d, m.y + Math.sin(a) * d - m.grow * m.unit * 0.03, m.unit * (0.012 + 0.012 * m.grow) * (1 + 0.3 * Math.sin(i * 1.7)));
    }
    g.fill({ color: pal.rim, alpha: m.fade * 0.28 });
    const s = Math.max(3, m.unit * 0.02);
    const ring = m.r * 1.2;
    for (let i = 0; i < FAIRY; i++) {
      const lit = clamp01((m.grow - (i / FAIRY) * 0.6) / 0.3);
      if (lit <= 0) continue;
      const a = (i / FAIRY) * TAU - Math.PI / 2;
      const x = m.x + Math.cos(a) * ring;
      const y = m.y + Math.sin(a) * ring;
      const tone = i % 2 === 0 ? pal.spot : pal.incoming.circle;
      g.circle(x, y - s * 0.4, s * 1.8);
      g.fill({ color: tone, alpha: m.fade * lit * 0.16 });
      toadstool(g, x, y, s);
      g.fill({ color: pal.rim, alpha: m.fade * lit * 0.75 });
      toadCap(g, x, y, s);
      g.fill({ color: tone, alpha: m.fade * lit * 0.9 });
    }
  },

  trail(g, x, y, size, angle) {
    g.circle(x, y, size * 0.8);
    g.circle(x + Math.cos(angle) * size * 1.6, y - size * 1.5, size * 0.38);
  },
};
